import { Component, Inject, OnInit } from '@angular/core';
import { InputType, MODAL_DATA, NgbActiveModal, NotificationService } from '@agilent/ui-element';
import { FormBuilder, FormGroup } from '@angular/forms';
import { OpenLabSystem, OpenLabSystemApiService } from '@app/api/openlab-system-api.service';
import { CustomValidators } from '@app/shared/custom-input-validators';
import Utils from '@app/utils/utils';
import OlssUtils from '@app/pages/openlab-systems/openlab-system-utils';

enum ErrorMessage {
    INVALID_SERVER_NAME = 'Please enter a valid server name',
    INVALID_SHARED_DOWNLOAD_LOCATION = 'Control characters, reserved words, trailing dots or < > : " / | ? * are not allowed',
    INVALID_SHARED_DOWNLOAD_USERNAME = 'Please enter a valid shared download username. (e.g user, user@domain.com, domain\\username, username@domain)'
}

export interface OlssConnectionType {
    name: string;
    value: string;
}

@Component({
    selector: 'app-add-ols',
    template: `
        <ag-dialog
            [header]="isNew ? 'Register Server' : 'Edit Server'">
            <ag-dialog-content>

                <form [formGroup]="olssForm">
                    <!-- Server Information -->
                    <h3 class="section-title-with-line">Server Information</h3>
                    <app-input-wrapper
                        [required]="true"
                        formControlName="olss_server"
                        label="Server Name (FQDN)"
                        [description]="isNew
                        ? 'Enter fully qualified domain name for the OpenLab Server. Example: olserver.prod.example.com'
                        : 'All connected CIDS will need to be re-registered, if Server Name is changed'"
                        [errorMessage]="serverNameErrorMsg"
                    ></app-input-wrapper>

                    <app-input-wrapper
                        [required]="false"
                        formControlName="olss_description"
                        label="Description"
                    ></app-input-wrapper>

                    <!-- Connection Information -->
                    <h3 class="section-title-with-line">Connection Information</h3>
                    <app-dropdown-wrapper
                        id="connection-values-dropdown"
                        formControlName="olss_connection_type"
                        [values]="olssConnectionOptions"
                        [isDisabled]="olssForm.get('olss_server').invalid"
                        [required]="true"
                        bindProperty="value"
                        label="Connect to this server using"
                        description="Use the same convention as used by CDS Clients to connect to OpenLab server"
                    ></app-dropdown-wrapper>

                    <app-input-wrapper
                        [required]="true"
                        formControlName="olss_username"
                        label="Username"
                        description="This user is used for registering CIDs as instrument controllers in OLSS"
                    ></app-input-wrapper>

                    <app-input-wrapper
                        [required]="true"
                        formControlName="olss_password"
                        label="Password"
                        [valueType]="passwordInputType"
                        description="Password for the above user"
                    ></app-input-wrapper>

                    <!-- CID Network Share -->
                    <h3 class="section-title-with-line">CID Network Share</h3>
                    <app-input-wrapper
                        [required]="false"
                        formControlName="shared_download_location"
                        label="CID Network Share (\\\\server\\share)"
                        description="Optionally, provide a network file share path for CIDs to cache and share information. This optimizes internet download requirements and speeds up deployments.">
                    ></app-input-wrapper>

                    <div class="checkbox-wrapper">
                        <ag-checkbox
                            formControlName="use_anonymous_user"
                            [required]="false">
                        </ag-checkbox>
                        <p class="checkbox-description" [class.is-edit]="!isNew && olssForm.get('use_anonymous_user')?.value">
                            <span>Use Anonymous user</span>
                        </p>
                    </div>

                    <app-input-wrapper
                        [required]="false"
                        formControlName="shared_download_username"
                        label="CID Network Share Username"
                        description="This user is used to access the CID network share"
                        [disabled]="olssForm.get('use_anonymous_user').value">
                    </app-input-wrapper>

                    <app-input-wrapper
                        [required]="false"
                        formControlName="shared_download_password"
                        label="CID Network Share Password"
                        description="Password for the above user"
                        [valueType]="passwordInputType"
                        [disabled]="olssForm.get('use_anonymous_user').value">
                    </app-input-wrapper>

                    <!-- Reason for Change -->
                    <ng-container *ngIf="userReasonRequired">
                        <h3 class="section-title-with-line">Reason for change</h3>
                        <app-user-reason-form
                            id="edit-reason"
                            *ngIf="userReasonRequired"
                            [required]="userReasonRequired"
                            [disabled]="isLoading"
                            (formReady)="onReasonFormReady($event)"></app-user-reason-form>
                    </ng-container>
                </form>
            </ag-dialog-content>
            <ag-dialog-actions>
                <span *ngIf="isLoading">Please wait...</span>
                <ag-loader
                    id="loading-spinner"
                    class="inline-progress"
                    [visible]="isLoading"
                    [small]="true"></ag-loader>
                <ag-button
                    id="cancel-btn"
                    type="secondary"
                    (click)="cancel()"
                    value="Cancel">
                </ag-button>
                <ag-button
                    [disabled]="isSaveButtonDisabled()"
                    [class.disabled]="isSaveButtonDisabled()"
                    type="primary"
                    (click)="saveOpenLabSystem()"
                    [value]="isNew ? 'Save' : 'Update'"
                    id="save-btn">
                </ag-button>
            </ag-dialog-actions>
        </ag-dialog>
    `,
    styles: [
        `
            :host .inline-progress > ::ng-deep .loader-overlay {
                background: transparent !important;
                margin: 0;
                padding: 0;
                position: static;
                transform: scale(0.7);
            }
        `

    ]
})
export class AddOpenLabSystemComponent implements OnInit {

    olssForm: FormGroup;
    isLoading: boolean;
    customerId: number;
    isNew: boolean;
    isSubmitted: boolean;
    serverNameErrorMsg: string;
    openLabSystem: OpenLabSystem;
    originalOpenLabSystem: OpenLabSystem;
    userReasonRequired = false;
    use_anonymous_user: false;
    passwordInputType = InputType.PASSWORD;
    olssConnectionOptions: OlssConnectionType[] = [];

    constructor(
        private activeModal: NgbActiveModal,
        private openLabSystemApiService: OpenLabSystemApiService,
        private notificationService: NotificationService,
        @Inject(MODAL_DATA) private modalData: any,
        private formBuilder: FormBuilder
    ) {
    }

    ngOnInit() {
        const customerId = this.modalData.customerId;
        this.isSubmitted = false;
        this.isNew = (this.modalData.existingOpenLabSystem === undefined);
        if (this.isNew) {
            this.openLabSystem = {
                id: 0,
                is_deleted: false,
                customer_id: customerId,
                name: null,
                olss_server: null,
                olss_connection_type: null,
                olss_username: null,
                olss_password: null,
                olss_description: null,
                shared_download_location: null,
                use_anonymous_user: false,
                shared_download_username: null,
                shared_download_password: null,
                software_versions_json: null,
                is_locked: false
            };
        }
        else {
            this.openLabSystem = { ...this.modalData.existingOpenLabSystem };
            this.originalOpenLabSystem = { ...this.modalData.existingOpenLabSystem };
            this.userReasonRequired = true;
            this.olssConnectionOptions = OlssUtils.getOlssConnectionTypes(this.openLabSystem.olss_server);
        }

        this.olssForm = this.formBuilder.group(
            {
                olss_server: [
                    this.openLabSystem.olss_server,
                    [
                        CustomValidators.blankField('Server Name (FQDN)'),
                        CustomValidators.pattern(Utils.SERVER_FQDN_PATTERN, ErrorMessage.INVALID_SERVER_NAME)
                    ]
                ],
                olss_connection_type: [
                    this.getOlssConnectionStringByType(this.openLabSystem.olss_connection_type)
                ],
                olss_username: [
                    this.openLabSystem.olss_username,
                    [CustomValidators.blankField('Username')]
                ],
                olss_password: [
                    this.openLabSystem.olss_password,
                    [CustomValidators.blankField('Password')]
                ],
                olss_description: [ this.openLabSystem.olss_description ],
                shared_download_location: [
                    this.openLabSystem.shared_download_location,
                    [
                        CustomValidators.blankField('Shared Download Location'),
                        CustomValidators.maxLength(256, 'Network File Share Path cannot exceed 256 characters'),
                        CustomValidators.pattern(Utils.SHARED_DOWNLOAD_LOCATION_PATTERN, ErrorMessage.INVALID_SHARED_DOWNLOAD_LOCATION)
                    ]
                ],
                use_anonymous_user: [this.openLabSystem.use_anonymous_user],
                shared_download_username: [
                    this.openLabSystem.shared_download_username,
                    [CustomValidators.blankField('Shared Download Username'),
                     CustomValidators.pattern(Utils.SHARED_DOWNLOAD_USERNAME_PATTERN, ErrorMessage.INVALID_SHARED_DOWNLOAD_USERNAME)
                    ]
                ],
                shared_download_password: [
                    this.openLabSystem.shared_download_password,
                    [CustomValidators.blankField('Shared Download Password')]
                ],
                reason: []
            }
        );

        // Subscribe to changes in the Server Name (FQDN) and update the "Connect using" dropdown.
        this.olssForm.get('olss_server').valueChanges
            .subscribe(async fqdn => {
                this.serverNameErrorMsg = null;
                this.olssForm.get('olss_connection_type').reset();
                this.olssConnectionOptions = OlssUtils.getOlssConnectionTypes(fqdn);
        });
    }

    /**
     * Calls the api to save the new OpenLab Server data on the server
     */
    async saveOpenLabSystem() {
        // Enable form validation labels
        this.isSubmitted = true;

        // Validate the form fields
        if (this.olssForm.invalid) {
            return;
        }

        this.openLabSystem.name = this.olssForm.value.olss_server.trim();
        this.openLabSystem.olss_server = this.olssForm.value.olss_server.trim();
        this.openLabSystem.olss_connection_type = this.olssForm.value.olss_connection_type.name;
        this.openLabSystem.olss_username = this.olssForm.value.olss_username;
        this.openLabSystem.olss_password = this.olssForm.value.olss_password;
        this.openLabSystem.olss_description = this.olssForm.value.olss_description;
        if (this.olssForm.value.shared_download_location) {
            this.openLabSystem.shared_download_location = this.olssForm.value.shared_download_location.trim();
        }
        else {
            this.openLabSystem.shared_download_location = this.olssForm.value.shared_download_location;
        }
        this.openLabSystem.use_anonymous_user = this.olssForm.value.use_anonymous_user;
        if (!this.openLabSystem.use_anonymous_user) {
            this.openLabSystem.shared_download_username = this.olssForm.value.shared_download_username;
            this.openLabSystem.shared_download_password = this.olssForm.value.shared_download_password;
        }

        Utils.trimObjectValues(this.openLabSystem, ['olss_password']);

        // Disable all input fields
        this.olssForm.disable({emitEvent: false});

        // Save the openLabSystem object
        this.isLoading = true;
        try {
            let resp = null;
            if (this.isNew) {
                resp = await this.openLabSystemApiService.createOpenLabSystem({
                    openlab_system: this.openLabSystem
                });
            }
            else {
                const update_obj = { ...this.openLabSystem };

                Object.keys(this.originalOpenLabSystem).forEach(key => {
                    if (this.originalOpenLabSystem[key] === update_obj[key]) {
                        delete update_obj[key];
                    }
                });

                // Check if we have anything remaining to update
                if (Object.keys(update_obj).length === 0) {
                    // nothing to update
                    this.activeModal.dismiss();
                    this.notificationService.warning('Server fields will not be updated because no changes were detected');
                    return;
                }
                // Add the ID
                update_obj.id = this.openLabSystem.id;
                resp = await this.openLabSystemApiService.updateOpenLabSystem({
                    openlab_system: update_obj,
                    reason: this.olssForm.get('reason').value
                });
            }
            // Pass the value returned from the server back to the UI
            const messageVerb = this.isNew ? 'created' : 'updated';
            this.activeModal.close({
                openlabSystem: resp.openlab_system,
                message: `OpenLab server ${messageVerb}`
            });
        }
        catch (ex) {
            console.error('Save OpenLab Server error', ex);
            const createServerErrorValue = ex.data && ex.data.error;
            if (createServerErrorValue === 'olss_name_must_be_unique_per_customer') {
                this.serverNameErrorMsg = 'A server with the same Server Name (FQDN) already exists';
                this.isLoading = false;
                this.olssForm.enable({emitEvent: false});
            }
            else {
                this.activeModal.dismiss(ex.data.error);
            }
        }
    }

    cancel() {
        this.activeModal.dismiss();
    }

    onReasonFormReady(reasonForm: FormGroup) {
        this.olssForm.setControl('reason', reasonForm.get('userReason'));
    }

    isSaveButtonDisabled() {
        return (
            !this.olssForm.dirty ||
            this.olssForm.invalid
        );
    }

    private getOlssConnectionStringByType(olss_connection_type: string) {
        if (!olss_connection_type) {
            return;
        }
        return this.olssConnectionOptions
            .find(type => type.name === olss_connection_type);
    }
}
